import { NewDemandSplitButton } from '../components/NewDemandSplitButton'
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DndContext,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  Input,
  Option,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ReOrderDotsVertical16Regular, Search20Regular, Dismiss16Regular } from '@fluentui/react-icons'
import { useDemands, useReorderDemands, useTransitionDemand } from '../hooks/useDemands'
import type { Demand, DemandStage, DemandTransitionInput } from '../types/demand'
import {
  resolutionTypeLabel,
  priorityBucketColor,
  priorityTierLabel,
  stageLabel,
  PRIORITY_BUCKETS,
  DEMAND_TYPE_LABELS,
  SUPPORT_MODE_LABELS,
} from '../lib/labels'
import { STAFFING_FILTER_OPTIONS } from '../lib/demandFilters'
import { useFaTags } from '../hooks/useFocusAreas'
import { daysInStage, slaTone } from '../lib/demandSla'
import { deriveStaffing, STAFFING_STATUS_LABELS } from '../lib/staffing'
import { ErrorCard } from '../components/ErrorCard'
import { useToast } from '../components/ToastProvider'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { FOCUS_AREAS } from '../types/focus_area'
import { DemandIntakeForm } from '../components/DemandIntakeForm'
import { TransitionDialog, STAGE_LABEL, type BroadcastAudience } from '../components/TransitionDialog'
import { bucketColors } from '../theme'
import { ResolveDialog } from '../components/ResolveDialog'
import { demandDetailPath, openDemandInNewTab, isNewTabModifier } from '../lib/demandNav'
import { apiErrorMessage } from '../lib/apiError'
import { parseStageWarnings, type StageWarnings } from '../lib/stageWarnings'
import { isSuggestedNext } from '../lib/stageHints'
import { StageWarningDialog } from '../components/StageWarningDialog'
import { useOpenInNewTabMenu, OpenInNewTabMenu } from '../components/OpenInNewTabMenu'
import { CaretakerFilter } from '../components/CaretakerFilter'

/**
 * Demand Kanban — mockup §6825-6923.
 *
 * 5 columns in fixed horizontal order. Cards drag between columns and
 * call useTransitionDemand. Dropping into "resolved" opens a resolve
 * dialog (a substitute is rendered locally here; if a dedicated
 * ResolveDialog component lands later, swap the import).
 */

/* ─── Column model ─────────────────────────────────────────────── */

interface KanbanColumn {
  key: DemandStage
  title: string
  isTerminal?: boolean
  isParked?: boolean
}

// Titles come from the canonical label map — an inline literal here is how the
// "Parking" / "Parking Lot" drift got in (see lib/labels.ts, issue #772).
const COLUMNS: readonly KanbanColumn[] = [
  { key: 'new', title: stageLabel('new') },
  { key: 'scoping', title: stageLabel('scoping') },
  { key: 'triaging', title: stageLabel('triaging') },
  { key: 'resourcing', title: stageLabel('resourcing') },
  { key: 'closed', title: stageLabel('closed'), isTerminal: true },
  { key: 'parking', title: stageLabel('parking'), isParked: true },
]

/* ─── Styles ────────────────────────────────────────────────────── */

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  toolbarHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  toolbarRight: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  scrollWrap: {
    position: 'relative',
  },
  scrollBtn: {
    position: 'absolute',
    top: 0,
    bottom: tokens.spacingVerticalM,
    width: '28px',
    background: 'rgba(255, 255, 255, 0.95)',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    zIndex: 5,
    fontFamily: 'inherit',
    fontSize: '22px',
    lineHeight: 1,
    color: tokens.colorBrandForeground1,
    boxShadow: tokens.shadow8,
    opacity: 0,
    pointerEvents: 'none',
    transition: `opacity 200ms ease, background 100ms ease`,
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollBtnVisible: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  scrollBtnLeft: { left: '-8px' },
  scrollBtnRight: { right: '-8px' },
  board: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalM,
    overflowX: 'auto',
    paddingBottom: tokens.spacingVerticalM,
    scrollBehavior: 'smooth',
    scrollbarWidth: 'thin',
  },
  column: {
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '400px',
  },
  columnTerminal: {
    background: tokens.colorNeutralBackground2,
  },
  columnParked: {
    opacity: 0.6,
    borderLeft: `2px dashed ${tokens.colorNeutralStroke2}`,
  },
  colHeader: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalS}`,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  stageDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  colTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  colCount: {
    background: tokens.colorNeutralStroke2,
    color: tokens.colorNeutralForeground2,
    padding: '1px 8px',
    borderRadius: tokens.borderRadiusCircular,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    marginLeft: 'auto',
  },
  colTag: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    padding: '0 6px',
    borderRadius: tokens.borderRadiusSmall,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  colBody: {
    padding: tokens.spacingHorizontalS,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    overflowY: 'auto',
    minHeight: '60px',
  },
  colBodyOver: {
    background: tokens.colorBrandBackground2,
    outline: `2px dashed ${tokens.colorBrandStroke1}`,
    outlineOffset: '-4px',
    borderRadius: tokens.borderRadiusMedium,
  },
  colBodyDeny: {
    background: '#fef2f2',
    outline: `2px dashed ${bucketColors.highPriority}`,
    outlineOffset: '-4px',
    borderRadius: tokens.borderRadiusMedium,
  },
  colBodyDenyLabel: {
    textAlign: 'center',
    color: bucketColors.highPriority,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    padding: tokens.spacingVerticalS,
    background: '#fef2f2',
    borderRadius: `${tokens.borderRadiusMedium} ${tokens.borderRadiusMedium} 0 0`,
    marginBottom: tokens.spacingVerticalXS,
  },
  boardScrollLeft: {
    boxShadow: 'inset 10px 0 12px -8px rgba(43, 107, 166, 0.3)',
  },
  boardScrollRight: {
    boxShadow: 'inset -10px 0 12px -8px rgba(43, 107, 166, 0.3)',
  },
  empty: {
    color: tokens.colorNeutralForegroundDisabled,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'center',
    padding: `${tokens.spacingVerticalL} 0`,
  },
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
    boxShadow: tokens.shadow2,
    cursor: 'pointer',
    transitionProperty: 'border-color, box-shadow, transform',
    transitionDuration: '100ms',
    position: 'relative',
    ':hover': {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      boxShadow: tokens.shadow4,
      transform: 'translateY(-1px)',
    },
  },
  cardDragging: {
    opacity: 0.4,
  },
  cardTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.4,
    margin: `0 0 ${tokens.spacingVerticalS}`,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  cardId: {
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
  },
  cardBottom: {
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  ownerBlock: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
  },
  cardNames: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
  },
  urgencyDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '4px',
    verticalAlign: 'middle',
  },
  dragHint: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    color: tokens.colorNeutralForeground4,
    opacity: 0.5,
    cursor: 'grab',
  },
  daysGreen: { color: tokens.colorPaletteGreenForeground2, fontWeight: tokens.fontWeightSemibold },
  daysAmber: { color: tokens.colorPaletteDarkOrangeForeground1, fontWeight: tokens.fontWeightSemibold },
  daysRed:   { color: tokens.colorPaletteRedForeground1, fontWeight: tokens.fontWeightSemibold },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
})

/* ─── Stage dot colors ─────────────────────────────────────────── */

// Mockup button color maps to the Bayer brand-80 shade (#4c6a80).

const STAGE_DOT_COLOR: Record<DemandStage, string> = {
  new: '#64748b',
  under_review: '#f59e0b',
  open_to_application: '#0891b2',
  staffed: '#8b5cf6',
  resolved: bucketColors.quickWins,
  not_prioritized: bucketColors.declined,
  // New-model stages (FE expand, Inc-3).
  scoping: '#4f7cac',
  triaging: '#f59e0b',
  resourcing: '#0891b2',
  parking: bucketColors.declined,
  closed: bucketColors.quickWins,
}

/* ─── Priority bucket chip (matches DemandList badge style) ─────
 *
 * Reads `demand.priority_bucket`, which the server derives and has always
 * sent. This file used to carry its own snake_case vocabulary and derivation,
 * identical to DemandList's — two private copies of one thing, and both
 * disagreed with the server they were copying. See lib/labels.ts.
 */

/* ─── Suggested next stages — TINT ONLY, not a gate (#1049) ───────
 *
 * The table moved to lib/stageHints, shared with ChangeStageDialog. It used to
 * live here as a private copy, and the two copies had drifted apart on their
 * legacy rows — rows neither the backend nor this file needed. See that module
 * for what was wrong with them.
 *
 * Used purely to colour the drop zone. Every cross-column drop is permitted
 * (see the drop handler); an off-path one asks for a written reason.
 */

/* ─── Helpers ──────────────────────────────────────────────────── */

// daysInStage + slaTone come from lib/demandSla (#1156). This file's private
// daysBucket put Resourcing on a 14-day red line while the list used 60 —
// the same demand was red here and neutral there.

/**
 * Compact owner display name — full name if reasonably short, otherwise
 * first name + last initial. Empty string falls back to null.
 */
function ownerDisplayName(fullName: string | null | undefined): string | null {
  if (!fullName) return null
  const trimmed = fullName.trim()
  if (!trimmed) return null
  if (trimmed.length <= 22) return trimmed
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return trimmed
  const first = parts[0]
  const lastInitial = parts[parts.length - 1]![0]!.toUpperCase()
  return `${first} ${lastInitial}.`
}

/* ─── Card ─────────────────────────────────────────────────────── */

interface KanbanCardProps {
  demand: Demand
  isTerminal: boolean
  readOnly: boolean
  onNavigate: (id: number, event?: MouseEvent<HTMLElement>) => void
  onContextMenu: (id: number, event: MouseEvent<HTMLElement>) => void
}

function KanbanCard({ demand, isTerminal, readOnly, onNavigate, onContextMenu }: KanbanCardProps) {
  const styles = useStyles()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `card-${demand.id}`,
    data: { demandId: demand.id, sourceStage: demand.stage },
    disabled: isTerminal || readOnly,
  })

  const days = daysInStage(demand)
  const bucket = slaTone(demand.stage, days)
  const dayClass = bucket === 'green' ? styles.daysGreen : bucket === 'amber' ? styles.daysAmber : styles.daysRed
  const urgencyDotColor = bucket === 'red' ? bucketColors.highPriority : bucket === 'amber' ? '#f59e0b' : 'transparent'
  const priority = demand.priority_bucket ?? null
  const ownerName = ownerDisplayName(demand.caretaker_name)
  const requestorName = demand.requestor_name ?? null

  // Stage-aware emphasis (v2.8.0 slice 5): a card's limited real-estate leans
  // into what matters at its stage — scoping needs a caretaker, triaging needs a
  // priority, resourcing shows staffing progress.
  const staffing = deriveStaffing(demand)
  const needsCaretaker =
    demand.stage === 'scoping' && !demand.caretaker_name && !demand.caretaker_id
  const needsPriority = demand.stage === 'triaging' && !priority
  const inResourcing = demand.stage === 'resourcing'
  const staffingColor =
    staffing.status === 'fully_staffed'
      ? 'success'
      : staffing.status === 'partially_staffed'
        ? 'warning'
        : 'informative'

  const style: CSSProperties = {
    transform: isDragging
      ? `${CSS.Transform.toString(transform) ?? ''} rotate(2deg)`
      : CSS.Transform.toString(transform) ?? undefined,
    transition,
  }

  // Click-vs-drag is delegated to dnd-kit's PointerSensor activation
  // distance (6px). A pointer-down that doesn't move ≥6px bubbles up
  // as a normal click → onClick fires. A drag activates dnd-kit, which
  // calls preventDefault on the underlying event and suppresses click.
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.card} ${isDragging ? styles.cardDragging : ''}`}
      data-demand-id={demand.id}
      data-source-stage={demand.stage}
      data-testid={`kcard-${demand.id}`}
      {...(isTerminal || readOnly ? {} : listeners)}
      {...(readOnly ? {} : attributes)}
      role="link"
      aria-disabled={undefined}
      tabIndex={0}
      onClick={(e) => onNavigate(demand.id, e)}
      onContextMenu={(e) => onContextMenu(demand.id, e)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onNavigate(demand.id)
        }
      }}
    >
      {!isTerminal && !readOnly && (
        <span className={styles.dragHint} title="Drag to move" aria-hidden="true">
          <ReOrderDotsVertical16Regular />
        </span>
      )}
      <div className={styles.cardTitle}>{demand.title}</div>
      <div className={styles.cardMeta}>
        {demand.demand_id ? <span className={styles.cardId}>{demand.demand_id}</span> : null}
        {demand.focus_area_code ? (
          <Badge appearance="outline" size="small">
            {demand.focus_area_code}
          </Badge>
        ) : null}
        {priority ? (
          <Badge
            appearance="tint"
            size="small"
            data-testid={`kcard-priority-${demand.id}`}
            data-bucket={priority}
            style={{
              background: `${priorityBucketColor(priority)}1a`,
              color: priorityBucketColor(priority) ?? undefined,
            }}
          >
            {priorityTierLabel(priority)}
          </Badge>
        ) : null}
        {needsCaretaker && (
          <Badge appearance="tint" color="warning" size="small" data-testid={`kcard-needs-caretaker-${demand.id}`}>
            Needs caretaker
          </Badge>
        )}
        {/* The other half of the shepherd signal (#1208): who has it. The
            board showed only the orphaned state; a council member scanning
            for "whose is this?" had to open every card. */}
        {(demand.caretaker_name || demand.caretaker_id) && (
          <Badge
            appearance="tint"
            color="brand"
            size="small"
            data-testid={`kcard-caretaker-${demand.id}`}
            title={`Caretaker: ${demand.caretaker_name ?? `user #${demand.caretaker_id}`}`}
          >
            {(demand.caretaker_name ?? '?')
              .split(/\s+/)
              .map((p) => p[0])
              .filter(Boolean)
              .slice(0, 2)
              .join('')
              .toUpperCase() || '?'}
          </Badge>
        )}
        {needsPriority && (
          <Badge appearance="tint" color="warning" size="small" data-testid={`kcard-needs-priority-${demand.id}`}>
            Needs priority
          </Badge>
        )}
        {inResourcing && (
          <Badge
            appearance="tint"
            color={staffingColor}
            size="small"
            data-testid={`kcard-staffing-${demand.id}`}
            title={
              staffing.confirmed
                ? STAFFING_STATUS_LABELS[staffing.status]
                : `${STAFFING_STATUS_LABELS[staffing.status]} (unconfirmed — staffing may be arranged outside the app)`
            }
          >
            {staffing.filled}/{staffing.headcount} staffed
            {staffing.assessing ? ` · ${(demand.application_count ?? 0) - staffing.approved} pending` : ''}
          </Badge>
        )}
        {/* App-count is subsumed by the staffing badge while resourcing. */}
        {!inResourcing && (demand.application_count ?? 0) > 0 && (
          <Badge appearance="tint" color="informative" size="small">
            {demand.application_count} app{demand.application_count === 1 ? '' : 's'}
          </Badge>
        )}
        {(demand.tags ?? []).map((t) => (
          <Badge key={`tag-${t.id}`} appearance="outline" color="informative" size="small">
            {t.label}
          </Badge>
        ))}
      </div>
      <div className={styles.cardBottom}>
        {isTerminal ? (
          <>
            <span>{demand.resolved_at ? new Date(demand.resolved_at).toLocaleDateString() : ''}</span>
            <span>{resolutionTypeLabel(demand.resolution_type)}</span>
          </>
        ) : (
          <>
            <span className={dayClass}>
              <span
                className={styles.urgencyDot}
                style={{ background: urgencyDotColor }}
                aria-hidden="true"
              />
              {days}d in stage
            </span>
            <span className={styles.cardNames}>
              {requestorName && (
                <span
                  className={styles.ownerBlock}
                  title={`Requestor: ${requestorName}`}
                  data-testid={`kcard-requestor-${demand.id}`}
                >
                  <Avatar name={requestorName} size={20} color="colorful" />
                </span>
              )}
              <span
                className={styles.ownerBlock}
                title={demand.caretaker_name ? `Caretaker: ${demand.caretaker_name}` : 'Unassigned'}
                data-testid={`kcard-owner-${demand.id}`}
              >
                {ownerName ? (
                  <Avatar name={ownerName} size={20} color="colorful" />
                ) : (
                  <Avatar size={20} color="neutral" />
                )}
              </span>
            </span>
          </>
        )}
      </div>
    </div>
  )
}

/* ─── Column ───────────────────────────────────────────────────── */

interface KanbanColumnViewProps {
  column: KanbanColumn
  demands: Demand[]
  /** Server total for this stage over the whole filtered set (#1269).
   *  When it exceeds what loaded, the header says "n of N" instead of
   *  passing the loaded count off as the total. */
  serverTotal?: number
  readOnly: boolean
  onNavigate: (id: number, event?: MouseEvent<HTMLElement>) => void
  onContextMenu: (id: number, event: MouseEvent<HTMLElement>) => void
  activeDemand: Demand | null
}

function KanbanColumnView({
  column,
  demands,
  serverTotal,
  readOnly,
  onNavigate,
  onContextMenu,
  activeDemand,
}: KanbanColumnViewProps) {
  const styles = useStyles()
  const { setNodeRef, isOver } = useDroppable({ id: column.key, data: { stage: column.key } })

  // Determine drop zone visual state. All cross-column drops are
  // allowed now — non-natural transitions just open the dialog asking
  // for a reason (override path). We still show a warning hue + label
  // so the council sees that a reason will be required before dropping.
  let bodyDropClass = ''
  let showDenyLabel = false
  if (isOver && activeDemand && activeDemand.stage !== column.key) {
    if (isSuggestedNext(activeDemand.stage, column.key)) {
      bodyDropClass = styles.colBodyOver
    } else {
      bodyDropClass = styles.colBodyDeny
      showDenyLabel = true
    }
  }

  const columnClass = [
    styles.column,
    column.isTerminal ? styles.columnTerminal : '',
    column.isParked ? styles.columnParked : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <section className={columnClass} data-testid={`kanban-column-${column.key}`} data-column-key={column.key}>
      <header className={styles.colHeader}>
        <span className={styles.stageDot} style={{ background: STAGE_DOT_COLOR[column.key] }} />
        <span className={styles.colTitle}>{column.title}</span>
        <span className={styles.colCount} data-testid={`kanban-count-${column.key}`}>
          {serverTotal != null && serverTotal > demands.length
            ? `${demands.length} of ${serverTotal}`
            : demands.length}
        </span>
        {column.isTerminal ? (
          <span className={styles.colTag} title="Terminal state — no transitions out">
            terminal
          </span>
        ) : null}
        {column.isParked ? (
          <span className={styles.colTag} title="Parked — re-enters Scoping">
            parked
          </span>
        ) : null}
      </header>
      <div
        ref={setNodeRef}
        className={`${styles.colBody} ${bodyDropClass}`}
        data-stage={column.key}
        data-testid={`kanban-body-${column.key}`}
      >
        {showDenyLabel && (
          <div className={styles.colBodyDenyLabel}>Reason required</div>
        )}
        <SortableContext
          items={demands.map((d) => `card-${d.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {demands.length === 0 ? (
            <div className={styles.empty}>No demands</div>
          ) : (
            demands.map((d) => (
              <KanbanCard
                key={d.id}
                demand={d}
                isTerminal={!!column.isTerminal}
                readOnly={readOnly}
                onNavigate={onNavigate}
                onContextMenu={onContextMenu}
              />
            ))
          )}
        </SortableContext>
      </div>
    </section>
  )
}

/* ─── Resolve dialog (placeholder) ─────────────────────────────── */

/* ─── Page ─────────────────────────────────────────────────────── */

export default function DemandKanban() {
  const styles = useStyles()
  const navigate = useNavigate()
  const toast = useToast()
  const effectiveUser = useEffectiveUser()

  const isAdmin =
    effectiveUser?.role === 'admin' || effectiveUser?.role === 'super_admin'
  const readOnly = !isAdmin && effectiveUser?.role !== 'council'

  // Right-click "Open in new tab" menu + Ctrl/Cmd-click handling (shared with
  // DemandList). Normal click keeps in-app navigation.
  const { menu: contextMenu, openMenu, closeMenu } = useOpenInNewTabMenu()
  const handleCardNavigate = (id: number, event?: MouseEvent<HTMLElement>) => {
    if (event && isNewTabModifier(event)) {
      event.preventDefault()
      openDemandInNewTab(id)
      return
    }
    navigate(demandDetailPath(id))
  }

  // RBAC: council defaults to own FA but can browse any FA / all (view-only
  // outside their own FA — edit gating is enforced per-demand + server-side).
  const [focusArea, setFocusArea] = useState(isAdmin ? '' : (effectiveUser?.focus_area_code ?? ''))
  const effectiveFa = focusArea

  // Field filters (#1242 UI, #1269 semantics) — server-side params now.
  const [bucketFilter, setBucketFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [supportFilter, setSupportFilter] = useState('')
  const [staffingFilter, setStaffingFilter] = useState('')
  const [tagFilter, setTagFilter] = useState<number | null>(null)
  const [mineOnly, setMineOnly] = useState(false)
  const [caretakers, setCaretakers] = useState<string[]>([])

  const queryFilters = useMemo(() => {
    // The field filters travel as server params (#1269): the set that arrives
    // IS the filtered set, and data.total / data.stage_counts speak for all
    // of it — not for what happened to load.
    const f: {
      page_size: number
      focus_area?: string
      demand_type?: string
      support_mode?: string
      staffing?: string
      tag_id?: number
      bucket?: string
      mine_caretaker?: boolean
      caretaker?: string
    } = { page_size: 500 }
    if (effectiveFa) f.focus_area = effectiveFa
    if (typeFilter) f.demand_type = typeFilter
    if (supportFilter) f.support_mode = supportFilter
    if (staffingFilter) f.staffing = staffingFilter
    if (tagFilter != null) f.tag_id = tagFilter
    if (bucketFilter) f.bucket = bucketFilter
    if (mineOnly) f.mine_caretaker = true
    else if (caretakers.length) f.caretaker = caretakers.join(',')
    return f
  }, [effectiveFa, typeFilter, supportFilter, staffingFilter, tagFilter, bucketFilter, mineOnly, caretakers])

  const { data, isLoading, error, refetch } = useDemands(queryFilters)
  const transitionMutation = useTransitionDemand()
  const reorderMutation = useReorderDemands()
  const [intakeOpen, setIntakeOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')


  // Tag catalogs are per-FA, so the tag dropdown only means something once a
  // focus area is picked; switching FA resets it (ids are FA-scoped).
  const faTagsQuery = useFaTags(effectiveFa || undefined)
  const faTags = effectiveFa ? (faTagsQuery.data ?? []) : []

  const hasFieldFilters = !!(
    bucketFilter ||
    typeFilter ||
    supportFilter ||
    staffingFilter ||
    tagFilter != null ||
    caretakers.length ||
    mineOnly
  )
  const clearFieldFilters = () => {
    setBucketFilter('')
    setTypeFilter('')
    setSupportFilter('')
    setStaffingFilter('')
    setTagFilter(null)
    setMineOnly(false)
    setCaretakers([])
  }

  const demands = useMemo(() => {
    // Field filters happened server-side (#1269). Only the search box narrows
    // locally — it is per-keystroke, and a round trip per key would fight the
    // typing. The column headers say when a column is showing part of itself.
    let items = data?.items ?? []
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      items = items.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          (d.demand_id ?? '').toLowerCase().includes(q) ||
          (d.description ?? '').toLowerCase().includes(q),
      )
    }
    return items
  }, [data?.items, searchQuery])

  // Group demands by stage in one pass
  const grouped = useMemo(() => {
    const map = new Map<DemandStage, Demand[]>()
    for (const col of COLUMNS) map.set(col.key, [])
    for (const d of demands) {
      const arr = map.get(d.stage)
      if (arr) arr.push(d)
    }
    // Sort by rank_order ascending, then by created_at descending so newest
    // first when rank is missing
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const ar = a.rank_order ?? Number.POSITIVE_INFINITY
        const br = b.rank_order ?? Number.POSITIVE_INFINITY
        if (ar !== br) return ar - br
        return (b.created_at ?? '').localeCompare(a.created_at ?? '')
      })
    }
    return map
  }, [demands])

  /* ─── Scroll arrow visibility ─────────────────────────────── */

  const boardRef = useRef<HTMLDivElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const updateArrowVisibilityFromPointer = useCallback((clientX: number) => {
    const wrap = wrapRef.current
    if (!wrap) return
    const rect = wrap.getBoundingClientRect()
    const fromLeft = clientX - rect.left
    const fromRight = rect.right - clientX
    setShowLeft(fromLeft >= 0 && fromLeft <= 80)
    setShowRight(fromRight >= 0 && fromRight <= 80)
  }, [])

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    updateArrowVisibilityFromPointer(e.clientX)
  }
  const handleMouseLeave = () => {
    setShowLeft(false)
    setShowRight(false)
  }

  const scrollBy = (delta: number) => {
    boardRef.current?.scrollBy({ left: delta, behavior: 'smooth' })
  }

  /* ─── DnD ─────────────────────────────────────────────────── */

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const [activeDemand, setActiveDemand] = useState<Demand | null>(null)
  const [resolveTarget, setResolveTarget] = useState<Demand | null>(null)
  const [transitionTarget, setTransitionTarget] = useState<{ demand: Demand; toStage: DemandStage } | null>(null)
  const [scrollEdge, setScrollEdge] = useState<'left' | 'right' | null>(null)
  // A 409 the mover can acknowledge and proceed through — unmet stage
  // expectations, or the return-to-New confirmation (#1142). Drag-and-drop hits
  // the same warnings as the detail-page action, and now surfaces the same
  // dialog instead of a generic toast. `pendingBody` is the move to re-submit
  // once acknowledged. (This replaced a dead `quality_check_failed` branch — the
  // backend has spoken `stage_warnings` since #988, so the old Close-only dialog
  // never opened.)
  const [stageWarnings, setStageWarnings] = useState<StageWarnings | null>(null)
  const pendingBody = useRef<DemandTransitionInput | null>(null)
  // The move the warning is about — kept because the TransitionDialog closes
  // (transitionTarget cleared) while its warning dialog is open, and the
  // acknowledged retry still needs to know which demand and target stage.
  const warnedMove = useRef<{ demand: Demand; toStage: DemandStage } | null>(null)

  const handleDragStart = (e: DragStartEvent) => {
    if (readOnly) return
    const id = e.active.data.current?.demandId as number | undefined
    if (id == null) return
    const d = demands.find((x) => x.id === id) ?? null
    setActiveDemand(d)
  }

  const handleDragMove = (e: DragMoveEvent) => {
    // Drag-to-edge auto-scroll: when active card's pointer is within 60px
    // of the board's left or right edge, scroll by 24px per dragover tick.
    const board = boardRef.current
    if (!board || !e.activatorEvent) return
    const rect = board.getBoundingClientRect()
    const ptr = (e.activatorEvent as PointerEvent | MouseEvent)
    const translated = e.active.rect.current.translated
    const currentX = translated ? translated.left + translated.width / 2 : ptr.clientX ?? rect.left
    const fromLeft = currentX - rect.left
    const fromRight = rect.right - currentX
    if (fromLeft >= 0 && fromLeft <= 60) {
      board.scrollBy({ left: -24, behavior: 'auto' })
      setScrollEdge('left')
    } else if (fromRight >= 0 && fromRight <= 60) {
      board.scrollBy({ left: 24, behavior: 'auto' })
      setScrollEdge('right')
    } else {
      setScrollEdge(null)
    }
  }

  const handleDragEnd = (e: DragEndEvent) => {
    const dragged = activeDemand
    setActiveDemand(null)
    setScrollEdge(null)
    if (!dragged || readOnly) return

    const overId = e.over?.id as string | undefined
    if (!overId) return

    // Determine if the drop target is a column or another card
    const overData = e.over?.data?.current
    const targetStage: DemandStage | undefined = overData?.stage ?? overData?.sourceStage

    // Cross-column: stage transition
    if (targetStage && targetStage !== dragged.stage) {
      if (dragged.stage === 'closed') return // terminal — cannot move

      if (targetStage === 'closed') {
        setResolveTarget(dragged)
        return
      }
      // Open the transition dialog regardless of whether the target is
      // in the natural-next list. The dialog's confirm handler passes
      // `override` if the transition needs it, so the backend accepts
      // the jump as long as the council supplies a reason.
      setTransitionTarget({ demand: dragged, toStage: targetStage })
      return
    }

    // Same-column: reorder. Not while a caretaker filter hides cards: the ranks
    // would be renumbered from the visible cards only and tie with the hidden ones.
    if (caretakers.length > 0) {
      toast.showToast({ title: 'Clear the caretaker filter to reorder', intent: 'info' })
      return
    }
    const stageCards = grouped.get(dragged.stage)
    if (!stageCards || stageCards.length < 2) return

    const activeCardId = `card-${dragged.id}`
    const overCardId = overId
    if (activeCardId === overCardId) return

    const oldIndex = stageCards.findIndex((d) => `card-${d.id}` === activeCardId)
    const newIndex = stageCards.findIndex((d) => `card-${d.id}` === overCardId)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(stageCards, oldIndex, newIndex)
    // Assign sequential rank_order values
    const items = reordered.map((d, i) => ({ id: d.id, rank_order: i + 1 }))
    reorderMutation.mutate(items, {
      onSuccess: () => {
        toast.showToast({
          title: 'Order saved',
          body: `${dragged.title} moved to position ${newIndex + 1}`,
          intent: 'success',
        })
      },
      onError: (err) => {
        toast.showToast({
          title: 'Could not save order',
          body: apiErrorMessage(err, 'Reorder failed'),
          intent: 'error',
        })
      },
    })
  }

  // One submit path for both the first attempt and the acknowledged retry, so
  // the two cannot drift on how they build the body or read the result.
  const submitTransition = (demand: Demand, toStage: DemandStage, body: DemandTransitionInput) => {
    pendingBody.current = body
    transitionMutation.mutate(
      { id: demand.id, body },
      {
        onSuccess: () => {
          toast.showToast({
            title: 'Demand moved',
            body: `${demand.title} → ${STAGE_LABEL[toStage]}`,
            intent: 'success',
          })
          setTransitionTarget(null)
          setStageWarnings(null)
          pendingBody.current = null
        },
        onError: (err: unknown) => {
          const warnings = parseStageWarnings(err)
          if (warnings) {
            // Acknowledgeable: hand it to the dialog rather than a dead-end toast.
            warnedMove.current = { demand, toStage }
            setStageWarnings(warnings)
            setTransitionTarget(null)
            return
          }
          toast.showToast({
            title: 'Could not move demand',
            body: apiErrorMessage(err, 'Server rejected the transition'),
            intent: 'error',
          })
        },
      },
    )
  }

  const handleTransitionConfirm = (rationale: string, broadcastAudience?: BroadcastAudience) => {
    if (!transitionTarget) return
    const { demand, toStage } = transitionTarget
    submitTransition(demand, toStage, {
      to_stage: toStage,
      notes: rationale,
      broadcast_audience: broadcastAudience,
    })
  }

  // Re-submit the same move, now carrying the acknowledgement.
  const handleAcknowledgeWarnings = (acknowledgeWith: string[]) => {
    const move = warnedMove.current
    if (!move || !pendingBody.current) {
      setStageWarnings(null)
      return
    }
    submitTransition(move.demand, move.toStage, {
      ...pendingBody.current,
      acknowledged_warnings: acknowledgeWith,
    })
  }

  /* ─── Render ──────────────────────────────────────────────── */

  if (isLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner label="Loading demands…" />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorCard
        title="Could not load demands"
        message={apiErrorMessage(error, 'Unknown error')}
        retry={() => { void refetch() }}
      />
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarHeader}>
          <Text size={500} weight="semibold">
            Demand Pipeline
          </Text>
          <Text size={200}>
            Drag cards between columns to transition · Drag up/down to reorder ·{' '}
            {searchQuery.trim() || (data?.total ?? 0) <= demands.length
              ? `${demands.length} total`
              : `showing ${demands.length} of ${data?.total} — narrow the filters to see the rest`}
          </Text>
        </div>
        <div className={styles.toolbarRight}>
          <Input
            placeholder="Search demands…"
            value={searchQuery}
            onChange={(_, d) => setSearchQuery(d.value)}
            contentBefore={<Search20Regular />}
            contentAfter={
              searchQuery ? (
                <Dismiss16Regular
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSearchQuery('')}
                />
              ) : undefined
            }
            style={{ minWidth: '220px' }}
            data-testid="kanban-search"
          />
          <Dropdown
            placeholder="All focus areas"
            value={focusArea ? `${focusArea}` : ''}
            selectedOptions={focusArea ? [focusArea] : []}
            onOptionSelect={(_, d) => {
              setFocusArea(d.optionValue ?? '')
              // Tag ids are FA-scoped — a tag picked under FA1 means nothing
              // under FA2 or "All", so the tag filter resets with the FA.
              setTagFilter(null)
            }}
            aria-label="Filter by focus area"
            data-testid="kanban-fa-dropdown"
            style={{ minWidth: '180px' }}
          >
            <Option value="" text="All focus areas" key="__all__">
              All focus areas
            </Option>
            {FOCUS_AREAS.map((fa) => (
              <Option value={fa.code} text={`${fa.code} — ${fa.name}`} key={fa.code}>
                {fa.code} — {fa.name}
              </Option>
            ))}
          </Dropdown>
          {!readOnly && <NewDemandSplitButton onIntake={() => setIntakeOpen(true)} />}
        </div>
      </div>

      {/* Field filters (#1242) — same dimensions as the List view, shared
          rule in lib/demandFilters. A second row so the header row keeps
          search + FA + New demand at their familiar spots. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: tokens.spacingHorizontalS,
          alignItems: 'center',
          marginBottom: tokens.spacingVerticalS,
        }}
        data-testid="kanban-filter-row"
      >
        <Dropdown
          placeholder="All priorities"
          value={bucketFilter ? priorityTierLabel(bucketFilter) : ''}
          selectedOptions={bucketFilter ? [bucketFilter] : []}
          onOptionSelect={(_, d) => setBucketFilter(d.optionValue ?? '')}
          aria-label="Filter by priority bucket"
          data-testid="kanban-filter-bucket"
          style={{ minWidth: '150px' }}
        >
          <Option value="" text="All priorities" key="__all__">
            All priorities
          </Option>
          {PRIORITY_BUCKETS.map((key) => (
            <Option value={key} text={priorityTierLabel(key)} key={key}>
              {priorityTierLabel(key)}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          placeholder="All types"
          value={typeFilter ? (DEMAND_TYPE_LABELS[typeFilter] ?? typeFilter) : ''}
          selectedOptions={typeFilter ? [typeFilter] : []}
          onOptionSelect={(_, d) => setTypeFilter(d.optionValue ?? '')}
          aria-label="Filter by demand type"
          data-testid="kanban-filter-type"
          style={{ minWidth: '130px' }}
        >
          <Option value="" text="All types" key="__all__">
            All types
          </Option>
          {Object.entries(DEMAND_TYPE_LABELS).map(([key, label]) => (
            <Option value={key} text={label} key={key}>
              {label}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          placeholder="All support modes"
          value={supportFilter ? (SUPPORT_MODE_LABELS[supportFilter] ?? supportFilter) : ''}
          selectedOptions={supportFilter ? [supportFilter] : []}
          onOptionSelect={(_, d) => setSupportFilter(d.optionValue ?? '')}
          aria-label="Filter by support mode"
          data-testid="kanban-filter-support"
          style={{ minWidth: '150px' }}
        >
          <Option value="" text="All support modes" key="__all__">
            All support modes
          </Option>
          {Object.entries(SUPPORT_MODE_LABELS).map(([key, label]) => (
            <Option value={key} text={label} key={key}>
              {label}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          placeholder="Staffing: all"
          value={
            staffingFilter
              ? (STAFFING_FILTER_OPTIONS.find((o) => o.key === staffingFilter)?.label ?? '')
              : ''
          }
          selectedOptions={staffingFilter ? [staffingFilter] : []}
          onOptionSelect={(_, d) => setStaffingFilter(d.optionValue ?? '')}
          aria-label="Filter by staffing"
          data-testid="kanban-filter-staffing"
          style={{ minWidth: '140px' }}
        >
          <Option value="" text="Staffing: all" key="__all__">
            Staffing: all
          </Option>
          {STAFFING_FILTER_OPTIONS.map((o) => (
            <Option value={o.key} text={o.label} key={o.key}>
              {o.label}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          placeholder={effectiveFa ? 'All tags' : 'Tags (pick an FA)'}
          value={tagFilter != null ? (faTags.find((t) => t.id === tagFilter)?.label ?? '') : ''}
          selectedOptions={tagFilter != null ? [String(tagFilter)] : []}
          onOptionSelect={(_, d) => setTagFilter(d.optionValue ? Number(d.optionValue) : null)}
          aria-label="Filter by tag"
          data-testid="kanban-filter-tag"
          disabled={!effectiveFa}
          title={effectiveFa ? undefined : 'Tag catalogs are per focus area — pick one first'}
          style={{ minWidth: '140px' }}
        >
          <Option value="" text="All tags" key="__all__">
            All tags
          </Option>
          {faTags.map((t) => (
            <Option value={String(t.id)} text={t.label} key={t.id}>
              {t.label}
            </Option>
          ))}
        </Dropdown>
        <Button
          appearance={mineOnly ? 'primary' : 'subtle'}
          size="small"
          onClick={() => {
            setMineOnly((v) => !v)
            setCaretakers([])
          }}
          data-testid="kanban-filter-caretaking"
          title="Demands you caretake"
        >
          My caretaking
        </Button>
        <CaretakerFilter
          selected={caretakers}
          onChange={(selected) => {
            setCaretakers(selected)
            setMineOnly(false)
          }}
          focusArea={effectiveFa}
        />
        {hasFieldFilters && (
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss16Regular />}
            onClick={clearFieldFilters}
            data-testid="kanban-clear-filters"
          >
            Clear filters
          </Button>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
      >
        <div
          ref={wrapRef}
          className={styles.scrollWrap}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          data-testid="kanban-scroll-wrap"
        >
          <button
            type="button"
            className={`${styles.scrollBtn} ${styles.scrollBtnLeft} ${showLeft ? styles.scrollBtnVisible : ''}`}
            aria-label="Scroll left"
            data-testid="kanban-scroll-left"
            onClick={() => scrollBy(-240)}
          >
            {'‹'}
          </button>
          <button
            type="button"
            className={`${styles.scrollBtn} ${styles.scrollBtnRight} ${showRight ? styles.scrollBtnVisible : ''}`}
            aria-label="Scroll right"
            data-testid="kanban-scroll-right"
            onClick={() => scrollBy(240)}
          >
            {'›'}
          </button>
          <div
            className={`${styles.board} ${scrollEdge === 'left' ? styles.boardScrollLeft : ''} ${scrollEdge === 'right' ? styles.boardScrollRight : ''}`}
            ref={boardRef}
            data-testid="kanban-board"
          >
            {COLUMNS.map((col) => (
              <KanbanColumnView
                key={col.key}
                column={col}
                readOnly={readOnly}
                demands={grouped.get(col.key) ?? []}
                serverTotal={searchQuery.trim() ? undefined : data?.stage_counts?.[col.key]}
                onNavigate={handleCardNavigate}
                onContextMenu={openMenu}
                activeDemand={activeDemand}
              />
            ))}
          </div>
        </div>
      </DndContext>

      <ResolveDialog
        open={resolveTarget !== null}
        demandId={resolveTarget?.id ?? 0}
        demandRef={resolveTarget?.demand_id || `DEM-${resolveTarget?.id ?? ''}`}
        demandTitle={resolveTarget?.title ?? ''}
        onClose={() => setResolveTarget(null)}
        onResolved={() => {
          toast.showToast({
            title: 'Demand resolved',
            body: resolveTarget?.title ?? '',
            intent: 'success',
          })
          setResolveTarget(null)
        }}
      />

      <TransitionDialog
        open={transitionTarget !== null}
        demand={transitionTarget?.demand ?? null}
        toStage={transitionTarget?.toStage ?? null}
        onClose={() => setTransitionTarget(null)}
        onConfirm={handleTransitionConfirm}
        busy={transitionMutation.isPending}
      />

      <DemandIntakeForm
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        onSuccess={(demandId) => navigate(`/demand/${demandId}`)}
      />

      <StageWarningDialog
        warnings={stageWarnings}
        toStage={warnedMove.current?.toStage ?? null}
        busy={transitionMutation.isPending}
        onContinue={handleAcknowledgeWarnings}
        onGoFix={() => {
          setStageWarnings(null)
          warnedMove.current = null
          pendingBody.current = null
        }}
      />

      <OpenInNewTabMenu menu={contextMenu} onClose={closeMenu} />
    </div>
  )
}
